/**
 * CeoAlphaOmegaAdvisorPage — Phase 36.7b.
 *
 * ALPHA-OMEGA portfolio advisor report. LLM-backed; poll hourly.
 * Endpoint: GET /api/v1/internal/ceo/alpha-omega-advisor
 * F-1: all numbers come from the API; none are derived client-side.
 */

import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

export interface PortfolioHealthSnapshot {
  green: number;
  yellow: number;
  red: number;
}

export interface CashFlowForecast {
  period: string;
  projected_revenue_usd_cents: number;
  projected_costs_usd_cents: number;
  projected_net_usd_cents: number;
  confidence: string;
}

export interface SectorOpportunity {
  sector: string;
  urgency_score: number;
  novelty_score: number;
  signal_summary: string;
  suggested_action: string;
}

export interface AdvisorRisk {
  risk_type: string;
  description: string;
  severity: number;
}

export interface AlphaOmegaAdvisorReport {
  report_id: string;
  generated_at: number;
  narrative: string;
  portfolio_health: PortfolioHealthSnapshot;
  cash_flow_forecast: CashFlowForecast;
  sector_opportunities: SectorOpportunity[];
  pending_decisions_count: number;
  risks: AdvisorRisk[];
  recommendations: string[];
  cost_usd: number;
}

// ── Helpers (exported for tests) ─────────────────────────────────────────────

export function fmtUsdCents(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

export function riskSeverityColor(severity: number): string {
  if (severity <= 2) return '#fbbf24';
  if (severity === 3) return '#fb923c';
  return '#f87171';
}

export function cashFlowConfidenceColor(conf: string): string {
  switch (conf) {
    case 'high':   return '#34d399';
    case 'medium': return '#fbbf24';
    default:       return '#f87171';
  }
}

export function healthColor(n: number): string {
  if (n > 0) return '#34d399';
  return 'var(--omega-ink-subtle)';
}

export function fmtReportAt(secs: number): string {
  return new Date(secs * 1000).toLocaleString('en-US', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function CeoAlphaOmegaAdvisorPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['ceo', 'alpha-omega-advisor'],
    queryFn: () => apiFetch<AlphaOmegaAdvisorReport>('/internal/ceo/alpha-omega-advisor'),
    refetchInterval: 3_600_000,
    retry: false,
  });

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA',  to: '/omega' },
        { label: 'CEO GI', to: '/omega/ceo' },
        { label: 'Alpha-Omega Advisor' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', direction: 'ltr' }}>
          Alpha-Omega Advisor
        </h1>
        {data && (
          <span style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, fontSize: '8pt', padding: '1px 7px', color: 'var(--omega-ink-subtle)' }}>
            {fmtReportAt(data.generated_at)}
          </span>
        )}
        {data && data.cost_usd > 0 && (
          <span style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)' }}>
            ${data.cost_usd.toFixed(3)} LLM
          </span>
        )}
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Generating advisory report…</div>}
      {isError && (
        <div style={{ color: '#f87171', fontSize: '9pt', direction: 'ltr' }}>
          Error: {(error as Error).message}
        </div>
      )}

      {data && (
        <>
          {/* Portfolio health */}
          <section style={{ marginBottom: 24 }}>
            <SectionLabel label="Portfolio status" />
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <HealthCard label="Green — active"  value={data.portfolio_health.green}  color="#34d399" />
              <HealthCard label="Yellow — at risk" value={data.portfolio_health.yellow} color="#fbbf24" />
              <HealthCard label="Red — stopped"   value={data.portfolio_health.red}    color="#f87171" />
            </div>
          </section>

          {/* Cash flow forecast */}
          <section style={{ marginBottom: 24 }}>
            <SectionLabel label={`Cash flow — ${data.cash_flow_forecast.period}`} />
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <StatTile label="Projected revenue"  value={fmtUsdCents(data.cash_flow_forecast.projected_revenue_usd_cents)} />
              <StatTile label="Projected costs"  value={fmtUsdCents(data.cash_flow_forecast.projected_costs_usd_cents)} />
              <StatTile
                label="Projected net"
                value={fmtUsdCents(data.cash_flow_forecast.projected_net_usd_cents)}
                color={data.cash_flow_forecast.projected_net_usd_cents >= 0 ? '#34d399' : '#f87171'}
              />
              <div style={{
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${cashFlowConfidenceColor(data.cash_flow_forecast.confidence)}40`,
                borderRadius: 5, padding: '10px 14px',
              }}>
                <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, direction: 'ltr' }}>
                  Confidence
                </div>
                <div style={{ fontSize: '12pt', fontWeight: 700, color: cashFlowConfidenceColor(data.cash_flow_forecast.confidence), textTransform: 'uppercase' }}>
                  {data.cash_flow_forecast.confidence}
                </div>
              </div>
            </div>
          </section>

          {/* Sector opportunities */}
          {data.sector_opportunities.length > 0 && (
            <section style={{ marginBottom: 24 }}>
              <SectionLabel label="Market opportunities" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 680 }}>
                {data.sector_opportunities.map((opp, i) => (
                  <div key={opp.sector} style={{
                    display: 'grid', gridTemplateColumns: '1fr 70px 70px',
                    gap: 10, padding: '10px 14px',
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 4,
                  }}>
                    <div>
                      <div style={{ fontSize: '9pt', fontWeight: 700, marginBottom: 3, direction: 'ltr' }}>
                        <span style={{ color: '#7dd3fc', marginLeft: 8, fontFamily: 'var(--omega-mono)' }}>{i + 1}.</span>
                        {opp.sector.replace(/_/g, ' ')}
                      </div>
                      <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', direction: 'ltr' }}>{opp.signal_summary}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2, direction: 'ltr' }}>Urgency</div>
                      <div style={{ fontSize: '9pt', fontWeight: 700, color: opp.urgency_score >= 0.7 ? '#f87171' : '#fbbf24' }}>
                        {(opp.urgency_score * 100).toFixed(0)}%
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2, direction: 'ltr' }}>Novelty</div>
                      <div style={{ fontSize: '9pt', fontWeight: 700, color: '#7dd3fc' }}>
                        {(opp.novelty_score * 100).toFixed(0)}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Risks */}
          {data.risks.length > 0 && (
            <section style={{ marginBottom: 24 }}>
              <SectionLabel label="Risks" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 680 }}>
                {data.risks.map((risk, i) => (
                  <div key={i} style={{
                    display: 'grid', gridTemplateColumns: '50px 100px 1fr',
                    gap: 10, alignItems: 'center', padding: '8px 12px',
                    borderLeft: `3px solid ${riskSeverityColor(risk.severity)}`,
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4,
                  }}>
                    <span style={{
                      fontSize: '7.5pt', fontWeight: 700, padding: '1px 6px',
                      border: `1px solid ${riskSeverityColor(risk.severity)}`,
                      color: riskSeverityColor(risk.severity), borderRadius: 3, textAlign: 'center',
                    }}>
                      {risk.severity}/5
                    </span>
                    <span style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)', direction: 'ltr' }}>
                      {risk.risk_type}
                    </span>
                    <span style={{ fontSize: '8.5pt', direction: 'ltr' }}>{risk.description}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Recommendations */}
          {data.recommendations.length > 0 && (
            <section style={{ marginBottom: 24 }}>
              <SectionLabel label="Action recommendations (A-01 — founder decision)" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 680 }}>
                {data.recommendations.map((rec, i) => (
                  <div key={i} style={{
                    display: 'grid', gridTemplateColumns: '20px 1fr',
                    gap: 10, alignItems: 'flex-start', padding: '8px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                  }}>
                    <span style={{ fontSize: '8pt', color: '#7dd3fc', fontFamily: 'var(--omega-mono)', paddingTop: 1 }}>{i + 1}.</span>
                    <span style={{ fontSize: '9pt', direction: 'ltr' }}>{rec}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* LLM narrative */}
          <section style={{ marginBottom: 24 }}>
            <SectionLabel label="Narrative — Alpha-Omega Advisor" />
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6,
              padding: '16px 20px', maxWidth: 680,
              fontSize: '9pt', lineHeight: 1.7, direction: 'ltr', whiteSpace: 'pre-wrap',
            }}>
              {data.narrative}
            </div>
          </section>

          {/* Pending decisions banner */}
          {data.pending_decisions_count > 0 && (
            <div style={{
              background: 'rgba(251,191,36,0.06)',
              border: '1px solid rgba(251,191,36,0.3)',
              borderRadius: 6, padding: '10px 16px',
              fontSize: '9pt', color: '#fbbf24', direction: 'ltr', maxWidth: 680,
            }}>
              ⚠ {data.pending_decisions_count} decisions awaiting founder approval (A-01)
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionLabel({ label }: { label: string }) {
  return (
    <div style={{
      fontSize: '8.5pt', fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 10,
      fontFamily: 'var(--omega-mono)', direction: 'ltr',
    }}>
      {label}
    </div>
  );
}

function HealthCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{
      background: value > 0 ? `${color}0d` : 'rgba(255,255,255,0.03)',
      border: `1px solid ${value > 0 ? color + '40' : 'rgba(255,255,255,0.08)'}`,
      borderRadius: 6, padding: '12px 18px', minWidth: 110,
    }}>
      <div style={{ fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6, direction: 'ltr' }}>{label}</div>
      <div style={{ fontSize: '20pt', fontWeight: 700, color: value > 0 ? color : 'var(--omega-ink-subtle)' }}>{value}</div>
    </div>
  );
}

function StatTile({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '10px 14px',
    }}>
      <div style={{ fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5, direction: 'ltr' }}>{label}</div>
      <div style={{ fontSize: '14pt', fontWeight: 700, color: color ?? 'var(--omega-ink)', fontFamily: 'var(--omega-mono)' }}>{value}</div>
    </div>
  );
}
